import React, { useState, useMemo, useCallback, useEffect, Fragment } from 'react';
import T from 'prop-types';
import { mapValues, times } from 'lodash-es';

import ModalSharedReport from './ModalSharedReport';
import Tooltip from '../../../../components/Tooltip';
import Skeleton from '../../../../components/Skeleton';
import Button from '../../../../components/Buttons/Button';
import Pagination from '../../../../components/Pagination';
import { SortableTable } from '../../../../components/SortableTable';
import CsvExportButton from '../../../../components/Buttons/CsvExportButton';
import { toast } from '../../../../components/Toasts';

import { columns } from '../../configs/market-reports-config';

const mappedCadence = {
  one_off: 'One Off',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
};

const defaultMarketParams = { pdf_report_url: '', name: '', time_period: '' };

const MarketReportsTable = ({
  filters,
  tableData,
  isLoading,
  onFiltersChange,
}) => {
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [sharedParams, setSharedParams] = useState({});
  const [marketParams, setMarketParams] = useState(defaultMarketParams);

  const pagination = useMemo(() => (
    mapValues(filters.pagination, value => parseInt(value, 10))
  ), [filters.pagination]);

  const showPagination = pagination.total > pagination.per_page;

  useEffect(() => {
    if (sharedParams.shared) {
      toast.success(sharedParams.message, {
        onClose: () => setSharedParams({}),
      });
    }
  }, [sharedParams.shared]);

  const handleShareReportButton = useCallback((item) => {
    setMarketParams(item);
    setShareModalOpen(true);
  }, []);

  const handleReportShared = (shared, amountOfUsers) => {
    const message = <span>Your report was successfully shared with {amountOfUsers} user{amountOfUsers > 1 ? 's' : ''}</span>;

    setSharedParams({ shared, message });
  };

  const handleShareModalClose = useCallback(() => {
    setShareModalOpen(false);
    setMarketParams(defaultMarketParams);
  }, []);

  const loadingRows = useMemo(() => (
    times(pagination.per_page, n => ({
      key: n,
      name: <Skeleton height={16} width="60%" />,
      cadence: <Skeleton height={16} width="80%" />,
      time_period: <Skeleton height={16} />,
      benchmark_app: <Skeleton height={16} width="70%" />,
      actions: (
        <div className="report-actions-cell">
          <Skeleton height={32} width={32} />
          <Skeleton height={32} width={32} />
        </div>
      ),
    }))
  ), [pagination.per_page]);

  const rows = useMemo(() => {
    const renderActions = item => (
      <div className="report-actions-cell">
        <Tooltip content="Share" placement="topRight" wrapChildrenInHtmlNode>
          <Button
            variant="secondary"
            shape="square"
            size="sm"
            onClick={() => handleShareReportButton(item)}
          >
            <span className="hicon-share-normal" />
          </Button>
        </Tooltip>

        <CsvExportButton
          url={item.pdf_report_url}
          size="sm"
          tooltipText="Download"
        />
      </div>
    );

    return tableData.map(iteration => ({
      key: iteration.id,
      name: (
        <p className="text-truncate report-name-cell">
          {iteration.name}
        </p>
      ),
      cadence: mappedCadence[iteration.cadence] || iteration.cadence,
      time_period: iteration.time_period,
      benchmark_app: iteration.benchmark_app,
      actions: renderActions(iteration),
    }));
  }, [tableData]);

  // Handlers
  const handlePageChange = useCallback(({ selected }) => {
    onFiltersChange({ pagination: { ...pagination, current_page: selected } });
  }, [pagination]);

  const handleSort = useCallback(({ sortBy, sortOrder }) => {
    onFiltersChange({ order_by: sortBy, order_dir: sortOrder });
  }, []);

  return (
    <Fragment>
      <SortableTable
        tableClassName="table-default sticky-table-header market-reports-table"
        rows={isLoading ? loadingRows : rows}
        columns={columns}
        hasShowMoreCell={false}
        emptyStateText="No data to display"
        initialRowsLimit={pagination.per_page}
        showMoreCellColSpan={columns.length}
        defaultSortBy="status"
        sortOrder="asc"
        onSortEnd={handleSort}
      />
      {showPagination && (
        <Pagination
          total={pagination.total}
          perPage={pagination.per_page}
          currentPage={pagination.current_page}
          className="mt-sm"
          onPageChange={handlePageChange}
        />
      )}
      {shareModalOpen && (
        <ModalSharedReport
          open={shareModalOpen}
          report={marketParams}
          onShareSuccess={handleReportShared}
          onClose={handleShareModalClose}
        />
      )}
    </Fragment>
  );
};

MarketReportsTable.propTypes = {
  filters: T.object.isRequired,
  tableData: T.array.isRequired,
  isLoading: T.bool,
  onFiltersChange: T.func.isRequired,
};

export default MarketReportsTable;
